import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Helmet } from 'react-helmet';
import { LocationPermissionProvider } from "./context/LocationPermissionContext";
import PhoneScreen from "./screens/PhoneScreen";
import OtpScreen from "./screens/OtpScreen";
import HomeScreen from "./screens/HomeScreen";
import ProfileScreen from "./screens/ProfileScreen";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CheckChallan from "./screens/challan/CheckChallan";
import "./App.css";
import Search from "./screens/Search";
import ChallanDetails from "./screens/challan/ChallanDetails";
import SearchDetails from "./screens/searchDetails/SearchDetails";
import DrivingLicenseRenewal from "./screens/drivingLicense/DrivingLicenseRenewal";
import DuplicateRegistrationScreen from "./screens/duplicateRegistrationCertificate/DuplicateRegistrationScreen";
import BankNoc from "./screens/bankNOC/BankNoc";
import HighSecurityPlate from "./screens/highSecurityPlate/HighSecurityPlate";
import HypothecationTermination from "./screens/hypothecationTermination/HypothecationTermination";
import InterStateRTO from "./screens/interstateRTO/InterStateRTO";
import SellYourCar from "./screens/sellCar/SellYourCar";
import CarInsurance from "./screens/carInsurance/CarInsurance";
import BikeInsurance from "./screens/bikeInsurance/BikeInsurance";
import SellYourCarDetails from "./screens/sellCar/SellYourCarDetails";
import DLTest from "./screens/drivingLicense/DLTest/DLTest";
import AddYourVehicle from "./screens/addYourVehicle/AddYourVehicle";
import VehicleNoc from "./screens/vehicleNoc/VehicleNoc";
import VehicleReRegistration from "./screens/vehicleReRegistration/VehicleReRegistration";
import CashOnCar from "./screens/cashOnCar/CashOnCar";
import OwnershipTransfer from "./screens/ownershipTransfer/OwnershipTransfer";
import CarServiceReport from "./screens/carServiceReport/CarServiceReport";
import Loan from "./screens/loan/Loan";
import Garage from "./screens/garage/Garage";
import PrivacyPolicy from "./screens/PrivacyPolicy";
import TermsConditions from "./screens/TermsConditions";
import { isAuthenticated } from "./components/AuthUtils";
import BecomePartner from "./screens/becomePartner/BecomePartner";
import CashOnBike from "./screens/cashOnBike/CashOnBike";
import PucCertificate from "./screens/PUC Certificate/PucCertificate";
import AdhaarVerification from "./screens/adhaarVerification/AdhaarVerification";
import DLVerification from "./screens/adhaarVerification/DLVerification";
import Contact from "./screens/Contact";
import AboutUs from "./screens/AboutUs";
import Sitemap from "./screens/Sitemap";
import RefundPolicy from "./screens/RefundPolicy";
import CancellationPolicy from "./screens/CancellationPolicy";

function App() {
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    const authStatus = isAuthenticated();
    setIsAuth(authStatus);
  }, []);

  const updateAuthStatus = (status) => {
    setIsAuth(status);
  };

  const ProtectedRoute = ({ children }) => {
    if (!isAuthenticated()) {
      return <Navigate to="/phone" replace />;
    }
    return children;
  };

  const ConditionalHeader = () => {
    const location = useLocation();
    const shouldShowHeader =
      location.pathname === '/' ||
      location.pathname === '/home' ||
      location.pathname === '/searchDetails' ||
      isAuth;

    return shouldShowHeader ? <Header /> : null;
  };

  return (
    <LocationPermissionProvider>
      <Router>
        <div className="app">
          <Helmet>
            <meta charSet="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <title>VAAHANBUDDY - Vehicle Services Assistant for India</title>
            <meta
              name="description"
              content="Get all your vehicle-related tasks done easily with VAAHANBUDDY."
            />
          </Helmet>
          <ConditionalHeader />
          <div className="content">
            <Routes>
              <Route path="/" element={<HomeScreen />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route
                path="/phone"
                element={
                  isAuthenticated() ? (
                    <Navigate to="/" replace />
                  ) : (
                    <PhoneScreen setIsAuthenticated={updateAuthStatus} />
                  )
                }
              />
              <Route
                path="/otp"
                element={
                  isAuthenticated() ? (
                    <Navigate to="/" replace />
                  ) : (
                    <OtpScreen setIsAuthenticated={updateAuthStatus} />
                  )
                }
              />
              <Route path="/searchDetails" element={<SearchDetails />} />

              {/* Public routes */}
              <Route path="/about" element={<AboutUs />} />
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="/AdhaarVerification" element={<AdhaarVerification />} />
              <Route path="/DLVerification" element={<DLVerification />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-conditions" element={<TermsConditions />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/cancellation-policy" element={<CancellationPolicy />} />
              <Route path="/contact-form" element={<Contact />} />
              <Route path="/becomePartner" element={<BecomePartner />} />

              {/* All other routes require authentication */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfileScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/checkChallan"
                element={
                  <ProtectedRoute>
                    <CheckChallan />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/search"
                element={
                  <ProtectedRoute>
                    <Search />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/challanDetails"
                element={
                  <ProtectedRoute>
                    <ChallanDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/drivingLicenseRenewal"
                element={
                  <ProtectedRoute>
                    <DrivingLicenseRenewal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/DLTest"
                element={
                  <ProtectedRoute>
                    <DLTest />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/duplicateRegistrationCertificate"
                element={
                  <ProtectedRoute>
                    <DuplicateRegistrationScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bankNoc"
                element={
                  <ProtectedRoute>
                    <BankNoc />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/highSecurityPlate"
                element={
                  <ProtectedRoute>
                    <HighSecurityPlate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hypothecationTermination"
                element={
                  <ProtectedRoute>
                    <HypothecationTermination />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rtoNOC"
                element={
                  <ProtectedRoute>
                    <InterStateRTO />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellYourCar"
                element={
                  <ProtectedRoute>
                    <SellYourCar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sellYourCarDetails"
                element={
                  <ProtectedRoute>
                    <SellYourCarDetails />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/carInsurance"
                element={
                  <ProtectedRoute>
                    <CarInsurance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bikeInsurance"
                element={
                  <ProtectedRoute>
                    <BikeInsurance />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/addYourVehicle"
                element={
                  <ProtectedRoute>
                    <AddYourVehicle />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vehicleNoc"
                element={
                  <ProtectedRoute>
                    <VehicleNoc />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/vehicleReRegistration"
                element={
                  <ProtectedRoute>
                    <VehicleReRegistration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cashOnCar"
                element={
                  <ProtectedRoute>
                    <CashOnCar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cashOnBike"
                element={
                  <ProtectedRoute>
                    <CashOnBike />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ownershipTransfer"
                element={
                  <ProtectedRoute>
                    <OwnershipTransfer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pucCertificate"
                element={
                  <ProtectedRoute>
                    <PucCertificate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/carServiceReport"
                element={
                  <ProtectedRoute>
                    <CarServiceReport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/loan"
                element={
                  <ProtectedRoute>
                    <Loan />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/garage"
                element={
                  <ProtectedRoute>
                    <Garage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          <Footer />
        </div>
      </Router>
    </LocationPermissionProvider>
  );
}

export default App;
